import React from "react"
import { Canvas } from "@layerhub-io/react"
import Playback from "../Playback"
import useDesignEditorContext from "~/hooks/useDesignEditorContext"
import ContextMenu from "../ContextMenu"
import Cookies from 'js-cookie'
import { clearDropTargetOrDelete } from "~/utils/clearDropTarget"
import { useEditor } from "@layerhub-io/react"
import { getEditorGuideConfigs } from "~/utils/editorGuides"

export default function () {
  const { displayPlayback, displayPreview, currentScene, scenes, showDottedLine } = useDesignEditorContext()
  const editor = useEditor()
  const isDownload = Cookies.get('isDownload') === 'true'
  const sku = Cookies.get('photobookSku') || ""

  // Helper function to disable all canvas interactions - no renderAll to avoid loops
  const disableCanvasInteraction = React.useCallback(() => {
    if (!editor) return

    const canvas = editor.canvas?.canvas
    if (canvas) {
      canvas.selection = false
      canvas.forEachObject((obj: any) => {
        obj.selectable = false
        obj.evented = false
        obj.hoverCursor = 'default'
      })
      canvas.discardActiveObject()
      // Note: removed renderAll() to prevent infinite loops with event listeners
    }
  }, [editor])

  // Disable canvas interaction in download mode - runs on mount and scene changes
  React.useEffect(() => {
    if (editor && isDownload) {
      // Small delay to ensure scene has fully loaded
      const timeoutId = setTimeout(() => {
        disableCanvasInteraction()
        // Only call renderAll once after all changes
        editor.canvas?.canvas?.renderAll()
      }, 100)
      return () => clearTimeout(timeoutId)
    }
  }, [editor, isDownload, currentScene, disableCanvasInteraction])

  // Global keydown listener to intercept Delete/Backspace for drop targets
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Backspace" || e.key === "Delete") {
        const isInput = ["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName || "")
        const isContentEditable = document.activeElement?.hasAttribute("contenteditable")
        if (isInput || isContentEditable) return

        if (editor) {
          const activeObject = editor.objects.get()
          console.log("[Editor KeyDown] Backspace/Delete detected. Active object:", activeObject);

          if (activeObject && activeObject.type !== "activeSelection" && activeObject.metadata?.isDropTarget) {
            console.log("[Editor KeyDown] Intercepted drop target deletion.");
            e.preventDefault()
            e.stopPropagation()
            try {
              clearDropTargetOrDelete(editor, activeObject)
              console.log("[Editor KeyDown] clearDropTargetOrDelete executed successfully.");
            } catch (err) {
              console.error("[Editor KeyDown] Error executing clearDropTargetOrDelete:", err);
            }
          } else {
            console.log("[Editor KeyDown] Not a drop target or no active object. Let layerhub handle natively.");
          }
        }
      }
    }

    // Use capture phase to intercept before layerhub native deletion
    window.addEventListener("keydown", handleKeyDown, true)
    return () => window.removeEventListener("keydown", handleKeyDown, true)
  }, [editor])

  // Bleed / safe-zone guide lines
  React.useEffect(() => {
    if (!editor) return
    const bleedGuides = (editor as any).bleedGuides
    const safeZone = (editor as any).safeZone

    // Get the frame dimensions of the current scene from the editor canvas
    const frameObj = editor.canvas?.canvas?.getObjects()?.find((o: any) => o.type === 'Frame')
    const frameWidth = frameObj?.width || currentScene?.frame?.width || 0
    const frameHeight = frameObj?.height || currentScene?.frame?.height || 0

    if (!bleedGuides || !safeZone || !frameWidth || !frameHeight || !currentScene) {
      bleedGuides?.setEnabled(false)
      bleedGuides?.setConfig(null)
      safeZone?.setConfig(null)
      editor.canvas?.canvas?.requestRenderAll()
      return
    }

    const { bleedGuideConfig, safeZoneConfig } = getEditorGuideConfigs({
      sku,
      coverFinish: Cookies.get('cover_finish') || "",
      scenes,
      scene: currentScene,
      frame: {
        width: frameWidth,
        height: frameHeight,
      },
    })

    safeZone.setConfig(safeZoneConfig)

    bleedGuides.setEnabled(!displayPreview)
    bleedGuides.setConfig(
      bleedGuideConfig
        ? {
            ...bleedGuideConfig,
            showSpineGuides: showDottedLine,
          }
        : null
    )

    editor.canvas?.canvas?.requestRenderAll()
  }, [editor, showDottedLine, currentScene, scenes, displayPreview, sku])

  return (
    <div style={{ flex: 1, display: "flex", position: "relative", overflow: "hidden" }}>
      {displayPlayback && <Playback />}
      {!isDownload && <ContextMenu />}
      <Canvas
        config={{
          background: "#f1f2f6",
          controlsPosition: {
            rotation: "BOTTOM",
          },
          shadow: {
            blur: 4,
            color: "#fcfcfc",
            offsetX: 0,
            offsetY: 0,
          },
        }}
      />
    </div>
  )
}
